import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Checkbox } from "nlib/ui";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Attachments from "nlib/common/Attachments";
import Button from "nlib/ui/Button";
import Constants from "const/Constants";
import DocumentFileTypes from "const/DocumentFileTypes";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import DropArea from "nlib/common/DropArea";
import DropBoxPicker from "nlib/common/DropBoxPicker";
import GoogleDrivePicker from "nlib/common/GoogleDrivePicker";
import IntegrationServices from "const/IntegrationServices";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useState } from "react";
import Select from "nlib/ui/Select";
import SideBar, { SideBarContent, SideBarFooter, SideBarHeader } from "nlib/common/SideBar";
import Switcher from "nlib/ui/Switcher";
import Texts from "utils/Texts";
import UiActions from "actions/UiActions";

const { PNG, JPG, TIFF } = DocumentFileTypes;

const IMAGE_TYPE_EXTENSIONS = [PNG, JPG, TIFF].map(({ extensions }) => extensions).flat();

const FILE_DROP_FORMATS = Object.keys(DocumentFileTypes);

const FILE_DROP_ALLOWED_TYPES = Object.values(DocumentFileTypes)
  .reduce((aggregator, { extensions, mimeType }) => [...aggregator, ...extensions, mimeType], []);

const Sidebar = ({ editData, onClose }) => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const {
    extraData: { integrationService } = {},
    meta: { emailAddress }
  } = useSelector(getSelectedBusinessData);

  const [activeTypeTab, setActiveTypeTab] = useState(null);

  const [paymentType, setPaymentType] = useState(null);

  const [type, setType] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);

  const [merge, setMerge] = useState(false);

  const quickBooksBusiness = integrationService === IntegrationServices.QUICK_BOOKS.value;

  const options = useMemo(() => {
    return [
      { value: null, label: uiTexts.auto },
      { value: DocumentTypes.INVOICE, label: uiTexts.invoice },
      { value: DocumentTypes.BILL, label: uiTexts.bill },
      { value: DocumentTypes.RECEIPT, label: uiTexts.receipt },
      ...(quickBooksBusiness
        ? [{ value: DocumentTypes.SALES_RECEIPT, label: uiTexts.salesReceipt }]
        : [])
    ];
  }, [quickBooksBusiness, uiTexts]);

  const TypeSelectComponent = Platforms.checkIsTouchDevice() ? Select : Switcher;

  const attachments = useMemo(() => {
    if (!selectedFiles) return [];

    return selectedFiles.map((file, index) => ({ id: index, originalName: file.name || "", file }));
  }, [selectedFiles]);

  const handleTypeTabChange = useCallback((value) => {
    setActiveTypeTab(value);
    setType(value === DocumentTypes.BILL ? DocumentTypes.INVOICE : value);
    setPaymentType(
      (value === DocumentTypes.INVOICE || value === DocumentTypes.SALES_RECEIPT) ? DocumentPaymentTypes.SELL
        : (
          (value === DocumentTypes.BILL || value === DocumentTypes.RECEIPT) ? DocumentPaymentTypes.BUY : ""
        )
    );
  }, []);

  const handleMergeButtonClick = useCallback(() => {
    setMerge((prev) => !prev);
  }, []);

  const handleFilesDrop = useCallback((files, fileRejections) => {
    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files]);
    } else if (fileRejections.length) {
      setSelectedFiles([]);
    }
  }, []);

  const handleGoogleDrivePickerFilesPicked = useCallback((docs, oAuthToken) => {
    onClose({
      cloudService: "gdrive",
      oAuthToken,
      googleDocs: docs,
      type,
      paymentType,
      merge
    });
  }, [onClose, type, paymentType, merge]);

  const handleDropBoxPickerFilesPicked = useCallback((files) => {
    onClose({
      cloudService: "dropbox",
      filesLinks: files,
      type,
      paymentType,
      merge
    });
  }, [onClose, type, paymentType, merge]);

  const handleCloseButtonClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleImportButtonClick = useCallback(() => {
    onClose({
      merge,
      type,
      paymentType,
      files: selectedFiles
    });
  }, [onClose, merge, type, paymentType, selectedFiles]);

  const handleAttachmentClick = useCallback((item) => {
    const { originalName = "", file } = item;

    const typeImage = IMAGE_TYPE_EXTENSIONS.includes(`.${originalName.split(".").pop().toLowerCase()}`);

    if (typeImage && file) {
      dispatch(UiActions.showModalImages([URL.createObjectURL(file)]));
    }
  }, [dispatch]);

  const handleAttachmentDelete = useCallback((index) => {
    setSelectedFiles((prev) => {
      return prev.filter((file, fileIndex) => fileIndex !== index);
    });
  }, []);

  const handleEmailClick = useCallback(() => {
    navigator.clipboard.writeText(emailAddress);
    toast.info(uiTexts.copiedToClipboard);
  }, [emailAddress, uiTexts.copiedToClipboard]);

  return (
    <SideBar className={Css.sidebar}>
      <SideBarHeader onCloseClick={handleCloseButtonClick}>
        {editData ? uiTexts.editFile : uiTexts.uploadFiles}
      </SideBarHeader>
      <SideBarContent>
        <TypeSelectComponent
          className={Css.switcher}
          options={options}
          value={activeTypeTab}
          placeholder={uiTexts.selectType}
          onChange={handleTypeTabChange} />
        <Attachments
          showAll numbered
          useConfirm={false}
          attachments={attachments}
          onFileClick={handleAttachmentClick}
          onDelete={handleAttachmentDelete} />
        <DropArea
          multiple showAccepted={false}
          className={Css.dropArea}
          extensions={FILE_DROP_FORMATS}
          accept={FILE_DROP_ALLOWED_TYPES}
          hint={Texts.replaceVars(messages.documentUploadPageLimit, { pages: Constants.MAX_DOCUMENT_PAGES })}
          onDrop={handleFilesDrop} />
        {!Platforms.checkIsTouchDevice() && (
          <div className={Css.cloudDocumentsPickers}>
            <div className={Css.title}>{messages.uploadFromCloudStorage}:</div>
            <div className={Css.pickers}>
              <div>
                <GoogleDrivePicker onFilesPicked={handleGoogleDrivePickerFilesPicked} />
              </div>
              <DropBoxPicker onFilesPicked={handleDropBoxPickerFilesPicked} />
            </div>
          </div>
        )}
        <div className={Css.autoUploadEmailGroup}>
          <div>{messages.autoUploadDocuments}</div>
          <div className={Css.email} title={emailAddress} onClick={handleEmailClick}>
            <span>{emailAddress}</span>
            <Icons.Copy />
          </div>
        </div>
      </SideBarContent>
      <SideBarFooter>
        <div className={Css.footerContent}>
          <div className={Css.mergeCheckbox}>
            <Button outline large onClick={handleMergeButtonClick}>
              <Checkbox toggle value={merge}>
                {messages.mergeDocumentsCheckbox}
              </Checkbox>
            </Button>
          </div>
          <div className={Css.buttons}>
            <Button large outline onClick={handleCloseButtonClick}>
              {uiTexts.close}
            </Button>
            <Button
              primary large
              disabled={!selectedFiles.length || (merge && selectedFiles.length === 1)}
              onClick={handleImportButtonClick}>
              {selectedFiles.length
                ? Texts.replaceVars(
                  merge ? uiTexts.mergeCountDocuments : uiTexts.uploadCountDocuments,
                  { count: selectedFiles.length }
                )
                : (merge ? uiTexts.mergeDocuments : uiTexts.uploadDocuments)}
            </Button>
          </div>
        </div>
      </SideBarFooter>
    </SideBar>
  );
};

export default React.memo(Sidebar);
